import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) { return twMerge(clsx(inputs)) }

export function formatDate(date: string | null | undefined) {
  if (!date) return '—'
  const d = new Date(date)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'2-digit' })
}

export function formatCurrency(amount: number | null | undefined, currency = 'AED') {
  const n = typeof amount === 'number' ? amount : Number(amount)
  const safe = isNaN(n) ? 0 : n
  return `${currency} ${safe.toLocaleString('en-US', { minimumFractionDigits: 0 })}`
}

export function truncate(str: string, n: number) {
  return str.length > n ? str.slice(0, n) + '...' : str
}

export function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase()
}

// Picks the translation entry matching the current UI language from a DB
// `*_translations[]` array (e.g. city_translations, vendor_translations),
// falling back to English then the first entry. Use this instead of hardcoding
// `locale === 'en'` so Arabic UI shows Arabic DB translations when available.
export function pickTranslation(
  list: Array<{ locale?: string | null; name?: string; [key: string]: any }> | null | undefined,
  lang: string
): { locale?: string | null; name?: string; [key: string]: any } | undefined {
  if (!list?.length) return undefined
  const norm = lang?.toLowerCase()
  return list.find(tr => tr.locale?.toLowerCase() === norm)
    ?? list.find(tr => tr.locale?.toLowerCase() === 'en')
    ?? list[0]
}

// Picks a `_ar`/`_ur`-suffixed sibling field when present (e.g. `name_ar` next
// to `name` on a user/driver/booking row), falling back to the English field.
// Use for flat DB fields that have no `*_translations[]` array — person names,
// free-form enum values (payment_method_ar), etc.
export function localized(obj: any, field: string, lang: string, fallback = '—') {
  if (!obj) return fallback
  if (lang === 'ar' && obj[`${field}_ar`]) return obj[`${field}_ar`]
  if (lang === 'ur' && obj[`${field}_ur`]) return obj[`${field}_ur`]
  return obj[field] ?? fallback
}

// Like `localized`, but for objects that may carry a `*_translations[]` array
// (city/category/package) instead of — or in addition to — flat name fields.
export function resolveName(obj: any, lang: string, translationsField: string, fallback = '—') {
  if (!obj) return fallback
  const list = obj[translationsField]
  if (list?.length) return pickTranslation(list, lang)?.name ?? fallback
  return localized(obj, 'name', lang, fallback)
}

// Neutral person-silhouette placeholder shown wherever a profile photo is missing
// or fails to load. Inline SVG data-URI so it needs no network request and can't
// itself 404. Use via <Avatar> or the DUMMY_AVATAR constant directly on an <img>.
export const DUMMY_AVATAR =
  "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%20100%20100'%3E%3Crect%20width='100'%20height='100'%20fill='%23e5e7eb'/%3E%3Ccircle%20cx='50'%20cy='40'%20r='16'%20fill='%239ca3af'/%3E%3Cpath%20d='M50%2060c-16%200-28%2010-28%2026v8h56v-8c0-16-12-26-28-26z'%20fill='%239ca3af'/%3E%3C/svg%3E"

// Swap a broken <img> to the dummy avatar exactly once (guards against loops if
// the placeholder itself ever failed).
export function onAvatarError(e: { currentTarget: HTMLImageElement }) {
  const img = e.currentTarget
  if (img.src !== DUMMY_AVATAR) img.src = DUMMY_AVATAR
}
